import { PromoTile } from './index';
import { isVideo } from '../../utils/video';

export class PromoTileBuilder {
  /**
   * Create the promo tile DOM node and append to parent
   * @param {HTMLElement} parentNode - parent container element
   * @returns {PromoTileBuilder}
   */
  makeNode(parentNode) {
    const promoTileNode = document.createElement('div');
    promoTileNode.classList.add('promo-tile-c-basis');
    this.baseClassName && promoTileNode.classList.add(`${this.baseClassName}__promo-tile`);
    this.promoTileNode = promoTileNode;
    this.size > 1 && this.promoTileNode.classList.add(`promo-tile-c-basis__2-column`);
    parentNode.appendChild(promoTileNode);
    return this;
  }

  /**
   * Store BEM base class prefix for extra class naming
   * @param {String} className - BEM base class name
   * @returns {PromoTileBuilder}
   */
  makeExtraBaseClass(className) {
    this.baseClassName = className;
    return this;
  }

  /**
   * Store the promo tile image URL and alt text
   * @param {String} imageUrl - image source URL
   * @param {String} imageAlt - image alt text
   * @returns {PromoTileBuilder}
   */
  makeImage(imageUrl, imageAlt) {
    this.imageUrl = imageUrl || '';
    this.imageAlt = imageAlt || '';
    return this;
  }

  /**
   * Store optional video source URL
   * @param {String} videoSource - video source URL
   * @returns {PromoTileBuilder}
   */
  makeVideo(videoUrl) {
    const validation = isVideo(videoUrl);
    if (!validation.result) {
      return this;
    }

    this.videoUrl = videoUrl;
    return this;
  }

  /**
   * Store the click-through destination URL
   * @param {String} url - destination URL for the tile link
   * @returns {PromoTileBuilder}
   */
  makeDestinationUrl(url) {
    this.destinationUrl = url || '';
    return this;
  }

  /**
   * Store GTM tracking data for impression and click events
   * @param {String} trackingId - unique tracking identifier
   * @param {String} creativeName - promotion creative name
   * @param {Number} position - tile position in the grid
   * @returns {PromoTileBuilder}
   */
  makeTracking(trackingId, creativeName, position) {
    this.trackingId = trackingId;
    this.creativeName = creativeName;
    this.position = position;
    return this;
  }

  /**
   * Set Constructor tracking attributes on the promo tile node
   * @param {Object} product - Constructor API product object
   * @returns {PromoTileBuilder}
   */
  setConstructorAttributes(product) {
    if (!this.promoTileNode || !product) return this;

    const itemId = product.data?.id || '';
    const itemName = product.value || '';

    this.promoTileNode.setAttribute('data-cnstrc-item-id', itemId);
    this.promoTileNode.setAttribute('data-cnstrc-item-name', itemName);

    return this;
  }

  /**
   * Set the size property for the promo tile
   * @param {number} size - the size value for the promo tile
   * @returns {PromoTileBuilder}
   */
  makeSize(size) {
    this.size = size;
    return this;
  }

  /**
   * Set Constructor tracking attributes for autocomplete results
   * @param {Object} product - Constructor API autocomplete product object
   * @param {String} section - Section name (e.g., 'Products')
   * @returns {PromoTileBuilder} - PromoTileBuilder object
   */
  setConstructorAutocompleteAttributes(product, section = 'Products') {
    if (!this.promoTileNode || !product) return this;

    this.promoTileNode.setAttribute('data-cnstrc-item', 'autosuggest');
    this.promoTileNode.setAttribute('data-cnstrc-item-section', section);
    this.promoTileNode.setAttribute('data-cnstrc-item-id', product.data?.id || '');
    this.promoTileNode.setAttribute('data-cnstrc-item-name', product.value || '');

    return this;
  }

  /**
   * Make the promo tile a Constructor recommendation by setting the appropriate data attribute
   * @returns {PromoTileBuilder} - PromoTileBuilder object
   */
  makeConstructorRecommendation() {
    this.promoTileNode.setAttribute('data-cnstrc-item', 'recommendation');
    return this;
  }

  /**
   * Build a new PromoTile instance from this builder's properties
   * @returns {PromoTile}
   */
  build() {
    return new PromoTile(this);
  }
}
